import React, { useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import Button from "@material-ui/core/Button";
import Dialog from "@material-ui/core/Dialog";
import Stepper from "@material-ui/core/Stepper";
import Step from "@material-ui/core/Step";
import StepLabel from "@material-ui/core/StepLabel";
import StepContent from "@material-ui/core/StepContent";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogContentText from "@material-ui/core/DialogContentText";
import DialogTitle from "@material-ui/core/DialogTitle";
import MenuItem from "@material-ui/core/MenuItem";
import TextField from "@material-ui/core/TextField";
import Grid from "@material-ui/core/Grid";
import { makeStyles } from "@material-ui/core/styles";
import { useSnackbar } from "notistack";
import { useHistory } from "react-router-dom";
import * as yup from "yup";
import { useFormik } from "formik";
import GoogleMapReact from "google-map-react";
import List from "@material-ui/core/List";
import ListItem from "@material-ui/core/ListItem";
import ListSubheader from "@material-ui/core/ListSubheader";
import HomeIcon from "@material-ui/icons/HomeRounded";
import HelpIcon from "@material-ui/icons/Help";
import Tooltip from "@material-ui/core/Tooltip";
import useIpsExclusive from "../../../hooks/useIpsExclusive";

import LoadingButton from "../../Buttons/Loading";
import * as constants from "../../../constants";
import * as actions from "../../../store/actions";
import { formatAddress } from "../../../utils/address";
import {
  adminCreateProperty,
  getFormattedAddress,
  batchWriteDivisionRelations,
} from "../../../utils/appsyncOperations";
import { isUserRole } from "../../../utils/user";
import HelpAdornment from "../../InputAdornments/Help";
import * as colors from "../../../styles/colors";
import DivisionSelector from "../../DivisionSelector";

const createMapOptions = (maps) => {
  return {
    panControl: true,
    mapTypeControl: true,
    scrollwheel: true,
    zoomControl: true,
    fullscreenControl: false,
  };
};

const CreatePropertyStepper = ({ isOpen, onClose }) => {
  const classes = useStyles();

  const ipsExclusive = useIpsExclusive();

  const [activeStep, setActiveStep] = useState(0);
  const [nextButtonLoading, setNextButtonLoading] = useState(false);
  const [coordinates, setCoordinates] = useState(null);
  const [divisionIds, setDivisionIds] = useState([]);
  const serviceProviderId = useSelector(
    (state) => state.serviceProvider.serviceProvider.serviceProviderId
  );
  const enqueueSnackbar = useSnackbar().enqueueSnackbar;
  const dispatch = useDispatch();
  const history = useHistory();
  const steps = [
    "General",
    "Location Search",
    "Location Confirmation",
    <span className={classes.stepperHelpContainer}>
      Divisions
      <Tooltip title={constants.helpText.propertyDivisionAdd}>
        <HelpIcon
          className={classes.stepperHelp}
          color={"action"}
          fontSize={"small"}
        />
      </Tooltip>
    </span>,
  ];

  const generalForm = useFormik({
    initialValues: {
      displayName: "",
      variant: constants.property.variantInputs.COMMERCIAL.value,
    },
    onSubmit: (values) => generalFormSubmit(values),
    validationSchema: yup.object({
      displayName: yup
        .string()
        .max(140, "Must be 140 characters or less.")
        .required("Required"),
      variant: yup
        .string()
        .oneOf(
          Object.values(constants.property.variantInputs).map(
            (variant) => variant.value
          )
        ),
    }),
  });

  const generalFormSubmit = () => {
    handleNext();
  };

  const searchForm = useFormik({
    initialValues: {
      search: "",
    },
    onSubmit: (values) => searchFormSubmit(values),
    validationSchema: yup.object({
      search: yup
        .string()
        .max(140, "Must be 140 characters or less.")
        .required("Required"),
    }),
  });

  const searchFormSubmit = async (values) => {
    const { search } = values;
    setNextButtonLoading(true);
    try {
      const place = await getFormattedAddress(search);
      setCoordinates(place.coordinates);
      confirmationForm.setFieldValue(
        "streetNumber",
        place.addressComponents.streetNumber
      );
      confirmationForm.setFieldValue(
        "streetName",
        place.addressComponents.streetName
      );
      const subpremise = place.addressComponents.subpremise;
      confirmationForm.setFieldValue(
        "subpremise",
        subpremise ? subpremise : ""
      );
      confirmationForm.setFieldValue("city", place.addressComponents.city);
      confirmationForm.setFieldValue("state", place.addressComponents.state);
      confirmationForm.setFieldValue(
        "zipCode",
        place.addressComponents.zipCode
      );
      confirmationForm.setFieldValue(
        "country",
        place.addressComponents.country
      );
      handleNext();
    } catch (e) {
      searchForm.setFieldError(
        "search",
        `${e} Please modify your search and try again.`
      );
    }
    setNextButtonLoading(false);
  };

  const confirmationForm = useFormik({
    initialValues: {
      streetNumber: "",
      streetName: "",
      subpremise: "",
      city: "",
      state: "",
      country: "",
      zipCode: "",
    },
    onSubmit: (values) => confirmationFormSubmit(values),
    validationSchema: yup.object({
      streetNumber: yup
        .string()
        .max(140, "Must be 140 characters or less.")
        .required("Required"),
      streetName: yup
        .string()
        .max(140, "Must be 140 characters or less.")
        .required("Required"),
      subpremise: yup.string().max(140, "Must be 140 characters or less."),
      city: yup
        .string()
        .max(140, "Must be 140 characters or less.")
        .required("Required"),
      state: yup
        .string()
        .max(140, "Must be 140 characters or less.")
        .required("Required"),
      zipCode: yup
        .string()
        .max(140, "Must be 140 characters or less.")
        .required("Required"),
      country: yup
        .string()
        .max(140, "Must be 140 characters or less.")
        .required("Required"),
    }),
  });

  const confirmationFormSubmit = (values) => {
    handleNext();
  };

  const createProperty = async () => {
    if (divisionIds.length === 0)
      return enqueueSnackbar(constants.errors.noDivisionsOnProperty, {
        variant: "error",
      });
    setNextButtonLoading(true);
    const place = {
      coordinates,
      addressComponents: confirmationForm.values,
      formattedAddress: formatAddress(confirmationForm.values),
    };
    const input = {
      geofence: 50,
      place,
      displayName: generalForm.values.displayName,
      serviceProviderId,
      patrolWindows: [],
      variant: generalForm.values.variant,
    };

    try {
      const property = await adminCreateProperty(input);
      const { propertyId } = property;
      const divisionsInput = divisionIds.map((id) => ({
        divisionId: id,
        relationalId: propertyId,
        operation: "CREATE",
        variant: "PROPERTY",
        serviceProviderId,
      }));
      const divisionRelations = await batchWriteDivisionRelations(
        divisionsInput
      );
      setNextButtonLoading(false);
      dispatch(actions.properties.setProperty(property));
      dispatch(actions.divisions.setDivisions({ divisionRelations }));
      history.push(`/map/properties/${propertyId}`);
    } catch (e) {
      enqueueSnackbar(e, { variant: "error" });
      setNextButtonLoading(false);
    }
  };

  const getNextHandler = () => {
    switch (activeStep) {
      case 0:
        return generalForm.submitForm();
      case 1:
        return searchForm.submitForm();
      case 2:
        return confirmationForm.submitForm();
      case 3:
        return createProperty();
      default:
        return () => {};
    }
  };

  const handleNext = () => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const renderStepButtons = () => {
    return (
      <div className={classes.actionsContainer}>
        <>
          <Button
            disabled={activeStep === 0}
            onClick={handleBack}
            className={classes.button}
          >
            Back
          </Button>
          <LoadingButton
            variant="contained"
            color="primary"
            onClick={getNextHandler}
            className={classes.button}
            isLoading={nextButtonLoading}
          >
            {activeStep === steps.length - 1 ? "Finish" : "Next"}
          </LoadingButton>
        </>
      </div>
    );
  };

  const handleMapClick = ({ lat, lng }) => setCoordinates({ lat, lng });

  const renderLocationConfirmation = () => {
    const {
      subpremise,
      streetNumber,
      streetName,
      city,
      state,
      country,
      zipCode,
    } = confirmationForm.values;

    const streetNumberError =
      confirmationForm.touched.streetNumber &&
      confirmationForm.errors.streetNumber;
    const streetNameError =
      confirmationForm.touched.streetName && confirmationForm.errors.streetName;
    const subpremiseError =
      confirmationForm.touched.subpremise && confirmationForm.errors.subpremise;
    const cityError =
      confirmationForm.touched.city && confirmationForm.errors.city;
    const stateError =
      confirmationForm.touched.state && confirmationForm.errors.state;
    const zipCodeError =
      confirmationForm.touched.zipCode && confirmationForm.errors.zipCode;
    const countryError =
      confirmationForm.touched.country && confirmationForm.errors.country;

    return (
      <>
        <List>
          <ListSubheader disableSticky>Address name</ListSubheader>
          <ListItem>
            <Grid container spacing={2} justify="space-between">
              <Grid item xs={12} sm={3}>
                <TextField
                  id={"streetNumber"}
                  label="Street Number"
                  onBlur={confirmationForm.onBlur}
                  value={streetNumber}
                  onChange={confirmationForm.handleChange}
                  required={true}
                  variant="filled"
                  fullWidth
                  error={streetNumberError}
                  helperText={
                    streetNumberError
                      ? confirmationForm.errors.streetNumber
                      : null
                  }
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  id={"streetName"}
                  label="Street Name"
                  onBlur={confirmationForm.onBlur}
                  value={streetName}
                  onChange={confirmationForm.handleChange}
                  required={true}
                  variant="filled"
                  fullWidth
                  error={streetNameError}
                  helperText={
                    streetNameError ? confirmationForm.errors.streetName : null
                  }
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <TextField
                  id={"subpremise"}
                  label="Subpremise"
                  onBlur={confirmationForm.onBlur}
                  value={subpremise}
                  onChange={confirmationForm.handleChange}
                  variant="filled"
                  fullWidth
                  error={subpremiseError}
                  helperText={
                    subpremiseError ? confirmationForm.errors.subpremise : null
                  }
                  InputProps={{
                    startAdornment: (
                      <HelpAdornment
                        title={constants.helpText.propertySubpremise}
                      />
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <TextField
                  id={"city"}
                  label="City"
                  onBlur={confirmationForm.onBlur}
                  value={city}
                  onChange={confirmationForm.handleChange}
                  variant="filled"
                  fullWidth
                  error={cityError}
                  helperText={cityError ? confirmationForm.errors.city : null}
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <TextField
                  id={"state"}
                  label="State"
                  onBlur={confirmationForm.onBlur}
                  value={state}
                  onChange={confirmationForm.handleChange}
                  variant="filled"
                  fullWidth
                  error={stateError}
                  helperText={stateError ? confirmationForm.errors.state : null}
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <TextField
                  id={"zipCode"}
                  label="Zip Code"
                  onBlur={confirmationForm.onBlur}
                  value={zipCode}
                  onChange={confirmationForm.handleChange}
                  variant="filled"
                  fullWidth
                  error={zipCodeError}
                  helperText={
                    zipCodeError ? confirmationForm.errors.zipCode : null
                  }
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <TextField
                  id={"country"}
                  label="Country"
                  onBlur={confirmationForm.onBlur}
                  value={country}
                  onChange={confirmationForm.handleChange}
                  variant="filled"
                  fullWidth
                  error={countryError}
                  helperText={
                    countryError ? confirmationForm.errors.country : null
                  }
                />
              </Grid>
            </Grid>
          </ListItem>
          <ListSubheader disableSticky>
            Address location (click to adjust)
          </ListSubheader>
          <ListItem>
            <div className={classes.map}>
              <GoogleMapReact
                bootstrapURLKeys={{
                  key: process.env.REACT_APP_GOOGLE_MAPS_API_KEY || "",
                }}
                options={createMapOptions}
                defaultCenter={coordinates}
                defaultZoom={16}
                onClick={handleMapClick}
              >
                {coordinates && (
                  <div
                    className={classes.locationMarker}
                    lat={coordinates.lat}
                    lng={coordinates.lng}
                  >
                    <HomeIcon className={classes.locationMarkerIcon} />
                  </div>
                )}
              </GoogleMapReact>
            </div>
          </ListItem>
        </List>
        {renderStepButtons()}
      </>
    );
  };

  const renderLocation = () => {
    const searchError = searchForm.touched.search && searchForm.errors.search;
    return (
      <>
        <Grid container spacing={2} justify="space-between">
          <Grid item xs={12}>
            <TextField
              id={"search"}
              label="Property Address"
              onBlur={searchForm.onBlur}
              value={searchForm.values.search}
              onChange={searchForm.handleChange}
              required={true}
              variant="filled"
              fullWidth
              InputProps={{
                startAdornment: (
                  <HelpAdornment
                    title={constants.helpText.propertyAddressSearch}
                  />
                ),
              }}
              error={searchError}
              helperText={searchError ? searchForm.errors.search : null}
            />
          </Grid>
        </Grid>
        {renderStepButtons()}
      </>
    );
  };

  const renderGeneralInfo = () => {
    const displayNameError =
      generalForm.touched.displayName && generalForm.errors.displayName;
    const variantError =
      generalForm.touched.variant && generalForm.errors.variant;
    return (
      <>
        <Grid container spacing={2} justify="space-between">
          <Grid item xs={12} sm={9}>
            <TextField
              id={"displayName"}
              label="Display Name"
              onBlur={generalForm.onBlur}
              value={generalForm.values.displayName}
              onChange={generalForm.handleChange}
              required={true}
              variant="filled"
              fullWidth
              InputProps={{
                startAdornment: (
                  <HelpAdornment
                    title={constants.helpText.propertyDisplayName}
                  />
                ),
              }}
              error={displayNameError}
              helperText={
                displayNameError ? generalForm.errors.displayName : null
              }
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField
              select
              id={"variant"}
              label="Variant"
              onBlur={generalForm.onBlur}
              value={generalForm.values.variant}
              onChange={(e) =>
                generalForm.setFieldValue("variant", e.target.value)
              }
              fullWidth
              variant="filled"
              InputProps={{
                startAdornment: (
                  <HelpAdornment title={constants.helpText.propertyVariant} />
                ),
              }}
              error={variantError}
              helperText={variantError ? generalForm.errors.variant : null}
            >
              {Object.values(constants.property.variantInputs)
                .filter((variant) => {
                  if (
                    variant.value !== constants.property.variant.TECHNOLOGIES
                  ) {
                    return true;
                  }
                  return ipsExclusive;
                })
                .map((variant) => (
                  <MenuItem key={variant.value} value={variant.value}>
                    {variant.text}
                  </MenuItem>
                ))}
            </TextField>
          </Grid>
        </Grid>
        {renderStepButtons()}
      </>
    );
  };

  const renderDivisions = () => {
    return (
      <List>
        <ListSubheader>
          {constants.helpText.propertyDivisionSelection}
        </ListSubheader>
        <ListItem>
          <DivisionSelector
            selected={divisionIds}
            onSelectionChange={(ids) => setDivisionIds(ids)}
          />
        </ListItem>
        <ListItem>{renderStepButtons()}</ListItem>
      </List>
    );
  };

  const getStepContent = (step) => {
    switch (step) {
      case 0:
        return renderGeneralInfo();
      case 1:
        return renderLocation();
      case 2:
        return renderLocationConfirmation();
      case 3:
        return renderDivisions();
      default:
        return "Unknown step";
    }
  };

  const isAdmin = isUserRole({ admin: true });

  if (!isAdmin)
    return (
      <>
        <Dialog open={isOpen} onClose={onClose} maxWidth={"md"} fullWidth>
          <DialogTitle>Create property</DialogTitle>
          <DialogContent>
            <DialogContentText>You are not the correct role.</DialogContentText>
          </DialogContent>
        </Dialog>
      </>
    );

  return (
    <Dialog open={isOpen} onClose={onClose} maxWidth={"md"} fullWidth>
      <DialogTitle>Create Property</DialogTitle>
      <DialogContent>
        <Stepper activeStep={activeStep} orientation={"vertical"}>
          {steps.map((label, index) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
              <StepContent>{getStepContent(index)}</StepContent>
            </Step>
          ))}
        </Stepper>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="primary">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const useStyles = makeStyles((theme) => ({
  root: {
    width: "100%",
  },
  stepperHelpContainer: {
    display: "flex",
    flex: 1,
    justifyContent: "flex-start",
    alignItems: "center",
  },
  stepperHelp: {
    marginLeft: theme.spacing(1),
  },
  button: {
    marginTop: theme.spacing(1),
    marginRight: theme.spacing(1),
  },
  actionsContainer: {
    marginTop: theme.spacing(2),
  },
  resetContainer: {
    padding: theme.spacing(3),
  },
  divisionSelectionText: {
    marginBottom: theme.spacing(1),
  },
  map: {
    height: 400,
    width: "100%",
    borderRadius: 8,
    overflow: "hidden",
  },
  locationMarker: {
    height: 32,
    width: 32,
    borderRadius: 16,
    backgroundColor: colors.KONTRA_BLUE,
    display: "flex",
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
    transform: "translate(-50%,-50%)",
  },
  locationMarkerIcon: {
    color: "white",
    width: 24,
    height: 24,
  },
}));

export default CreatePropertyStepper;
